import { PDFDocument } from 'pdf-lib'
import { downloadBlob } from 'utils/download'

function buildPageFilename(
  originalFilename: string,
  startPage: number,
  endPage: number
): string {
  const dotIndex = originalFilename.lastIndexOf('.')
  const baseName =
    dotIndex > 0 ? originalFilename.slice(0, dotIndex) : originalFilename
  const extension = dotIndex > 0 ? originalFilename.slice(dotIndex) : '.pdf'

  const pageSuffix =
    startPage === endPage ? `_p${startPage}` : `_p${startPage}-${endPage}`

  return `${baseName}${pageSuffix}${extension}`
}

/**
 * Extracts a range of pages from a PDF and triggers a browser download.
 */
export async function extractAndDownloadPages(
  signedUrl: string,
  filename: string,
  startPage: number,
  endPage: number
): Promise<void> {
  const response = await fetch(signedUrl)
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`)
  }
  const sourceBytes = await response.arrayBuffer()

  const sourcePdf = await PDFDocument.load(sourceBytes)
  const outputPdf = await PDFDocument.create()

  const zeroStart = startPage - 1
  const zeroEnd = endPage - 1
  const pageIndices = Array.from(
    { length: zeroEnd - zeroStart + 1 },
    (_, i) => zeroStart + i
  )

  const copiedPages = await outputPdf.copyPages(sourcePdf, pageIndices)
  for (const page of copiedPages) {
    outputPdf.addPage(page)
  }

  const outputBytes = await outputPdf.save()
  const arrayBuffer = new ArrayBuffer(outputBytes.byteLength)
  new Uint8Array(arrayBuffer).set(outputBytes)
  const blob = new Blob([arrayBuffer], { type: 'application/pdf' })
  const outputFilename = buildPageFilename(filename, startPage, endPage)

  downloadBlob(blob, outputFilename)
}
